import {
  applyWaveYjsRuntimeFlag,
  WAVE_APP_ID,
  type WaveApiConfig,
} from "@vida/wave-core";
import { getClientConfigUrl, getRuntimeOceanBootstrap, setAppsyncRealtimeEndpoint } from "../config";
import { setPresignedBlobBaseUrl } from "./waveBlobDownload";
import { setRagSearchUrl } from "./waveRag";
import { setAdminApiBaseUrl } from "./waveAccountAPI";
import { setWaveWebPushVapidPublicKey } from "./webPush";

const WAVE_APP_ID_HEADER = "X-Vida-AppId";

type ClientConfigPayload = {
  wave_yjs_enabled?: boolean;
  wave_push_enabled?: boolean;
  wave_web_push_vapid_public_key?: string;
  cloud_appsync_realtime_endpoint?: string;
  pdf_presigned_url?: string;
  rag_search_url?: string;
  admin_api_url?: string;
  ocean_agent_stream_url?: string;
  wave_whale_agent_id?: string;
  cognito_identity_pool_id?: string;
  cognito_user_pool_id?: string;
  aws_region?: string;
};

const oceanStore = {
  streamUrl: "",
  whaleAgentId: "",
  identityPoolId: "",
  userPoolId: "",
  awsRegion: "",
};

function viteEnv(key: string): string {
  const raw = import.meta.env as Record<string, string | undefined>;
  return typeof raw[key] === "string" ? raw[key]!.trim() : "";
}

/** Prod: `vida-cloud-wave-config.json`. Dev: variabili VITE_* (solo se campo ancora vuoto). */
export function applyOceanEnvOverrides(): void {
  const fromRuntime = getRuntimeOceanBootstrap();
  const stream =
    fromRuntime.streamUrl || viteEnv("VITE_OCEAN_AGENT_STREAM_URL");
  const whaleId =
    fromRuntime.whaleAgentId || viteEnv("VITE_WAVE_WHALE_AGENT_ID");
  const identityPool =
    fromRuntime.identityPoolId || viteEnv("VITE_COGNITO_IDENTITY_POOL_ID");
  const userPool =
    fromRuntime.userPoolId || viteEnv("VITE_COGNITO_USER_POOL_ID");
  const region = fromRuntime.awsRegion || viteEnv("VITE_AWS_REGION");

  if (!oceanStore.streamUrl && stream) oceanStore.streamUrl = stream;
  if (!oceanStore.whaleAgentId && whaleId) oceanStore.whaleAgentId = whaleId;
  if (!oceanStore.identityPoolId && identityPool) oceanStore.identityPoolId = identityPool;
  if (!oceanStore.userPoolId && userPool) oceanStore.userPoolId = userPool;
  if (!oceanStore.awsRegion && region) oceanStore.awsRegion = region;
}

function clientConfigUrl(): string | null {
  const url = getClientConfigUrl();
  return url || null;
}

export type WhaleConfigField =
  | "ocean_agent_stream_url"
  | "wave_whale_agent_id"
  | "cognito_identity_pool_id"
  | "cognito_user_pool_id"
  | "aws_region";

export function getWhaleConfigMissing(): WhaleConfigField[] {
  const missing: WhaleConfigField[] = [];
  if (!oceanStore.streamUrl.trim()) missing.push("ocean_agent_stream_url");
  if (!oceanStore.whaleAgentId.trim()) missing.push("wave_whale_agent_id");
  if (!oceanStore.identityPoolId.trim()) missing.push("cognito_identity_pool_id");
  if (!oceanStore.userPoolId.trim()) missing.push("cognito_user_pool_id");
  if (!oceanStore.awsRegion.trim()) missing.push("aws_region");
  return missing;
}

export function whaleConfigMissingLabel(field: WhaleConfigField): string {
  switch (field) {
    case "ocean_agent_stream_url":
      return "URL stream Oceany (ocean_agent_stream_url)";
    case "wave_whale_agent_id":
      return "ID agente Whale in Currents (wave_whale_agent_id)";
    case "cognito_identity_pool_id":
      return "Cognito Identity Pool";
    case "cognito_user_pool_id":
      return "Cognito User Pool";
    case "aws_region":
      return "Regione AWS (aws_region)";
    default:
      return field;
  }
}

export function getOceanAgentStreamUrl(): string {
  return oceanStore.streamUrl;
}

export function getWaveWhaleAgentId(): string {
  return oceanStore.whaleAgentId;
}

export function getOceanCognitoIdentityPoolId(): string {
  return oceanStore.identityPoolId;
}

export function getOceanCognitoUserPoolId(): string {
  return oceanStore.userPoolId;
}

export function getOceanAwsRegion(): string {
  return oceanStore.awsRegion;
}

export function isWhaleConfigured(): boolean {
  return getWhaleConfigMissing().length === 0;
}

/** Allinea flag Yjs e endpoint realtime con la Lambda client-config (come iOS Wave). */
export async function fetchWaveClientConfig(cfg: WaveApiConfig): Promise<void> {
  applyOceanEnvOverrides();
  const url = clientConfigUrl();
  if (!url) return;

  try {
    const token = await cfg.getIdToken();
    const res = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        [WAVE_APP_ID_HEADER]: WAVE_APP_ID,
      },
    });
    if (!res.ok) return;

    const body = (await res.json()) as ClientConfigPayload;
    if (body.wave_yjs_enabled === true) {
      applyWaveYjsRuntimeFlag(true);
    } else if (body.wave_yjs_enabled === false) {
      applyWaveYjsRuntimeFlag(false);
    }
    if (body.cloud_appsync_realtime_endpoint?.trim()) {
      setAppsyncRealtimeEndpoint(body.cloud_appsync_realtime_endpoint);
    }
    if (body.pdf_presigned_url?.trim()) {
      setPresignedBlobBaseUrl(body.pdf_presigned_url);
    }
    if (body.rag_search_url?.trim()) {
      setRagSearchUrl(body.rag_search_url);
    }
    if (body.admin_api_url?.trim()) {
      setAdminApiBaseUrl(body.admin_api_url);
    }
    if (body.wave_web_push_vapid_public_key?.trim()) {
      setWaveWebPushVapidPublicKey(body.wave_web_push_vapid_public_key);
    }
    if (body.ocean_agent_stream_url?.trim()) {
      oceanStore.streamUrl = body.ocean_agent_stream_url.trim();
    }
    if (body.wave_whale_agent_id?.trim()) {
      oceanStore.whaleAgentId = body.wave_whale_agent_id.trim();
    }
    if (body.cognito_identity_pool_id?.trim()) {
      oceanStore.identityPoolId = body.cognito_identity_pool_id.trim();
    }
    if (body.cognito_user_pool_id?.trim()) {
      oceanStore.userPoolId = body.cognito_user_pool_id.trim();
    }
    if (body.aws_region?.trim()) {
      oceanStore.awsRegion = body.aws_region.trim();
    }
    applyOceanEnvOverrides();
    if (body.wave_push_enabled === false) {
      localStorage.setItem("wave_push_enabled", "0");
    } else if (body.wave_push_enabled === true) {
      localStorage.setItem("wave_push_enabled", "1");
    }
  } catch {
    /* best effort — polling GraphQL resta disponibile */
  }
}
